import { ApiResponse } from './enhancedApiClient';
import { config } from '../config/environment';

export interface TemporaryNumberingResult {
  temporary_item_numbers: string[];
  start_count: number;
  end_count: number;
  icm_count: string;
  item_type: string;
}

export interface TemporaryNumberingRequest {
  itemType: string;
  count: number;
}

class TemporaryNumberingService {
  private baseUrl = `${config.API_URL}`;

  /**
   * Get latest ICM counts for item types without generating temporary numbers
   */
  async getLatestICMCounts(): Promise<ApiResponse<Record<string, number>>> {
    try {
      const response = await fetch(`${this.baseUrl}/item-count-master`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      
      // Transform the ICM records into the expected format
      if (data.success && data.data) {
        const counts: Record<string, number> = {};
        data.data.forEach((record: any) => {
          counts[record.item_type] = parseInt(record.icm_count) || 0;
        });
        return {
          success: true,
          data: counts
        };
      }
      
      return data;
    } catch (error) {
      console.error('Error getting latest ICM counts:', error);
      throw error;
    }
  }

  /**
   * Update ICM counts for all item types
   */
  async updateICMCounts(counts: Record<string, number>): Promise<ApiResponse<Record<string, number>>> {
    try {
      // For now, we'll update each item type individually
      // This could be optimized with a batch update endpoint if available
      const updatePromises = Object.entries(counts).map(async ([itemType, count]) => {
        // First, get the ICM record for this item type
        const getResponse = await fetch(`${this.baseUrl}/item-count-master`, {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${localStorage.getItem('token')}`
          }
        });
        
        if (!getResponse.ok) {
          throw new Error(`Failed to get ICM record for ${itemType}`);
        }
        
        const getData = await getResponse.json();
        const icmRecord = getData.data?.find((record: any) => record.item_type === itemType);
        
        if (!icmRecord) {
          throw new Error(`ICM record not found for item type: ${itemType}`);
        }
        
        // Update the ICM count
        const updateResponse = await fetch(`${this.baseUrl}/item-count-master/${icmRecord.icm_id}/set-initial-count`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${localStorage.getItem('token')}`
          },
          body: JSON.stringify({ 
            initial_count: String(count).padStart(3, '0')
          })
        });
        
        if (!updateResponse.ok) {
          throw new Error(`Failed to update ICM count for ${itemType}`);
        }
        
        return { itemType, count };
      });
      
      await Promise.all(updatePromises);
      
      return {
        success: true,
        data: counts
      };
    } catch (error) {
      console.error('Error updating ICM counts:', error);
      throw error;
    }
  }

  /**
   * Generate temporary item numbers for order creation
   */
  async generateTemporaryNumbers(request: TemporaryNumberingRequest): Promise<ApiResponse<TemporaryNumberingResult>> {
    try {
      // Generate multiple numbers by calling the generate-item-number endpoint multiple times
      const numbers: string[] = [];
      const promises = [];
      
      for (let i = 0; i < request.count; i++) {
        promises.push(
          fetch(`${this.baseUrl}/item-count-master/generate-item-number`, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              'Authorization': `Bearer ${localStorage.getItem('token')}`
            },
            body: JSON.stringify({
              item_type: request.itemType
            })
          })
        );
      }
      
      const responses = await Promise.all(promises);
      
      for (const response of responses) {
        if (!response.ok) {
          const errorData = await response.json();
          throw new Error(errorData.message || 'Failed to generate item number');
        }
        
        const data = await response.json();
        if (data.success && data.data?.generated_item_number) {
          numbers.push(data.data.generated_item_number);
        }
      }
      
      // Get the current ICM count for this item type
      const icmResponse = await fetch(`${this.baseUrl}/item-count-master`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });
      
      let icmCount = '0';
      if (icmResponse.ok) {
        const icmData = await icmResponse.json();
        const icmRecord = icmData.data?.find((record: any) => record.item_type === request.itemType);
        if (icmRecord) {
          icmCount = icmRecord.icm_count;
        }
      }
      
      const result: TemporaryNumberingResult = {
        temporary_item_numbers: numbers,
        start_count: numbers.length > 0 ? 1 : 0,
        end_count: numbers.length,
        icm_count: icmCount,
        item_type: request.itemType
      };
      
      return {
        success: true,
        data: result
      };
    } catch (error) {
      console.error('Error generating temporary numbers:', error);
      throw error;
    }
  }

  /**
   * Rollback temporary item numbers when order is cancelled
   */
  async rollbackTemporaryNumbers(request: TemporaryNumberingRequest): Promise<ApiResponse<any>> {
    try {
      const response = await fetch(`${this.baseUrl}/rollback-numbers`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify(request)
      });

      const data = await response.json();
      
      if (!response.ok) {
        throw new Error(data.message || 'Failed to rollback temporary numbers');
      }

      return data;
    } catch (error) {
      console.error('Error rolling back temporary numbers:', error);
      throw error;
    }
  }

  /**
   * Finalize temporary item numbers when order is saved
   */
  async finalizeTemporaryNumbers(itemType: string, itemNumbers: string[]): Promise<ApiResponse<any>> {
    try {
      const response = await fetch(`${this.baseUrl}/finalize-numbers`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify({
          itemType,
          itemNumbers
        })
      });

      const data = await response.json();
      
      if (!response.ok) {
        throw new Error(data.message || 'Failed to finalize temporary numbers');
      }

      return data;
    } catch (error) {
      console.error('Error finalizing temporary numbers:', error);
      throw error;
    }
  }

  /**
   * Generate temporary numbers for multiple item types
   */
  async generateMultipleTemporaryNumbers(requests: TemporaryNumberingRequest[]): Promise<Record<string, TemporaryNumberingResult>> {
    const results: Record<string, TemporaryNumberingResult> = {};
    
    try {
      // Generate all temporary numbers in parallel
      const promises = requests.map(async (request) => {
        const result = await this.generateTemporaryNumbers(request);
        return {
          itemType: request.itemType,
          result: result.data
        };
      });

      const responses = await Promise.all(promises);
      
      responses.forEach(({ itemType, result }) => {
        results[itemType] = result;
      });

      return results;
    } catch (error) {
      console.error('Error generating multiple temporary numbers:', error);
      throw error;
    }
  }

  /**
   * Rollback multiple item types
   */
  async rollbackMultipleTemporaryNumbers(requests: TemporaryNumberingRequest[]): Promise<void> {
    try {
      // Rollback all temporary numbers in parallel
      const promises = requests.map(request => this.rollbackTemporaryNumbers(request));
      await Promise.all(promises);
    } catch (error) {
      console.error('Error rolling back multiple temporary numbers:', error);
      throw error;
    }
  }

  /**
   * Finalize multiple item types
   */
  async finalizeMultipleTemporaryNumbers(finalizationData: Record<string, string[]>): Promise<void> {
    try {
      // Finalize all temporary numbers in parallel
      const promises = Object.entries(finalizationData).map(([itemType, itemNumbers]) =>
        this.finalizeTemporaryNumbers(itemType, itemNumbers)
      );
      await Promise.all(promises);
    } catch (error) {
      console.error('Error finalizing multiple temporary numbers:', error);
      throw error;
    }
  }
}

export default new TemporaryNumberingService();
